import React, { useState } from 'react';
import { AlertCircle, X } from 'lucide-react';

const ALERT_FLAGS = {
  CHILDREN: {
    label: 'Children\'s Data',
    description: 'Processing personal data of minors (under 18 years old)',
    color: 'bg-red-500',
    borderColor: 'border-red-300 dark:border-red-700',
    bgColor: 'bg-red-50 dark:bg-red-950/30',
    textColor: 'text-red-900 dark:text-red-200',
    badge: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100',
  },
  HEALTH_DATA: {
    label: 'Health Data',
    description: 'Processing medical, clinical or health information',
    color: 'bg-red-500',
    borderColor: 'border-red-300 dark:border-red-700',
    bgColor: 'bg-red-50 dark:bg-red-950/30',
    textColor: 'text-red-900 dark:text-red-200',
    badge: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100',
  },
  BIOMETRIC_DATA: {
    label: 'Biometric Data',
    description: 'Processing fingerprint, facial, iris or other biometric information',
    color: 'bg-amber-500',
    borderColor: 'border-amber-300 dark:border-amber-700',
    bgColor: 'bg-amber-50 dark:bg-amber-950/30',
    textColor: 'text-amber-900 dark:text-amber-200',
    badge: 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-100',
  },
  AI_TOOLS: {
    label: 'AI/Automated Decision-Making',
    description: 'Using AI or automated systems to make decisions affecting individuals',
    color: 'bg-red-500',
    borderColor: 'border-red-300 dark:border-red-700',
    bgColor: 'bg-red-50 dark:bg-red-950/30',
    textColor: 'text-red-900 dark:text-red-200',
    badge: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100',
  },
  MONITORING: {
    label: 'Systematic Monitoring',
    description: 'Systematic surveillance, tracking or monitoring of data subjects',
    color: 'bg-red-500',
    borderColor: 'border-red-300 dark:border-red-700',
    bgColor: 'bg-red-50 dark:bg-red-950/30',
    textColor: 'text-red-900 dark:text-red-200',
    badge: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100',
  },
  EXTERNAL_ACCESS: {
    label: 'Third-Party Access',
    description: 'Data shared with external organizations or third parties',
    color: 'bg-amber-500',
    borderColor: 'border-amber-300 dark:border-amber-700',
    bgColor: 'bg-amber-50 dark:bg-amber-950/30',
    textColor: 'text-amber-900 dark:text-amber-200',
    badge: 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-100',
  },
  LARGE_SCALE: {
    label: 'Large Scale Processing',
    description: 'Processing data of more than 1,000 data subjects',
    color: 'bg-blue-500',
    borderColor: 'border-blue-300 dark:border-blue-700',
    bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    textColor: 'text-blue-900 dark:text-blue-200',
    badge: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100',
  },
  VULNERABLE_GROUPS: {
    label: 'Vulnerable Groups',
    description: 'Processing data of vulnerable individuals (children, elderly, disabled)',
    color: 'bg-red-500',
    borderColor: 'border-red-300 dark:border-red-700',
    bgColor: 'bg-red-50 dark:bg-red-950/30',
    textColor: 'text-red-900 dark:text-red-200',
    badge: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100',
  },
};

export default function AlertFlagsBanner({ riskFlags = [], reviewFlags = [], onDismiss }) {
  const [dismissed, setDismissed] = useState(new Set(reviewFlags || []));

  const handleDismiss = (flag) => {
    const newDismissed = new Set(dismissed);
    if (newDismissed.has(flag)) {
      newDismissed.delete(flag);
    } else {
      newDismissed.add(flag);
    }
    setDismissed(newDismissed);
    if (onDismiss) {
      onDismiss(Array.from(newDismissed));
    }
  };

  const visibleFlags = riskFlags.filter(flag => !dismissed.has(flag));

  if (visibleFlags.length === 0) {
    return null;
  }

  return (
    <div className={`rounded-lg border ${visibleFlags[0] ? ALERT_FLAGS[visibleFlags[0]]?.borderColor : 'border-destructive/30'} ${visibleFlags[0] ? ALERT_FLAGS[visibleFlags[0]]?.bgColor : 'bg-destructive/5'} p-4 mb-6`}>
      <div className="flex items-start gap-3">
        <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${visibleFlags[0] ? ALERT_FLAGS[visibleFlags[0]]?.textColor : 'text-destructive'}`} />
        <div className="flex-1">
          <h3 className={`font-semibold ${visibleFlags[0] ? ALERT_FLAGS[visibleFlags[0]]?.textColor : 'text-destructive'} mb-3`}>
            Assessment Alerts
          </h3>
          <div className="space-y-2">
            {visibleFlags.map(flag => {
              const config = ALERT_FLAGS[flag] || {};
              return (
                <div key={flag} className="flex items-start justify-between gap-2 group">
                  <div className="flex-1">
                    <div className={`inline-block px-2.5 py-1 rounded text-xs font-medium ${config.badge} mb-1`}>
                      {config.label}
                    </div>
                    <p className={`text-sm ${config.textColor}`}>
                      {config.description}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDismiss(flag)}
                    className={`flex-shrink-0 p-1.5 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100`}
                    title="Dismiss alert"
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            💡 These alerts highlight important data protection considerations. Review them carefully.
          </p>
        </div>
      </div>
    </div>
  );
}
